import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Bucket = ({ title, data, testid }) => {
  const entries = Object.entries(data || {});
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div className="bg-white rounded-lg border p-4" data-testid={testid}>
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</div>
      {entries.length === 0 && <div className="text-sm text-gray-400">No data yet</div>}
      <ul className="space-y-1.5">
        {entries.map(([k, v]) => (
          <li key={k} className="text-sm">
            <div className="flex justify-between"><span className="text-gray-700 truncate mr-3">{k}</span><span className="font-semibold text-gray-900">{v}</span></div>
            <div className="h-1 bg-gray-100 rounded"><div className="h-1 bg-[#722F37] rounded" style={{ width: `${(v / max) * 100}%` }} /></div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const VariantCard = ({ label, copy, data, isLeader }) => (
  <div className={`rounded-lg border p-4 ${isLeader ? 'border-[#FDB913] bg-[#FFFBEF]' : 'bg-white'}`} data-testid={`promo-variant-${label}`}>
    <div className="flex items-center justify-between">
      <div className="text-sm font-bold text-gray-900">Variant {label}</div>
      {isLeader && <span className="text-[10px] font-bold uppercase tracking-wide bg-[#FDB913] text-gray-900 px-2 py-0.5 rounded-full">Winning</span>}
    </div>
    <div className="text-xs text-gray-500 mt-0.5 truncate">"{copy}"</div>
    <div className="mt-3 flex items-end justify-between">
      <div>
        <div className="text-3xl font-bold text-[#722F37]" data-testid={`promo-${label}-rate`}>{data.rate}%</div>
        <div className="text-[11px] uppercase tracking-wide text-gray-500">signup rate</div>
      </div>
      <div className="text-right text-sm text-gray-700">
        <div><b data-testid={`promo-${label}-views`}>{data.views}</b> views</div>
        <div><b data-testid={`promo-${label}-submits`}>{data.submits}</b> signups</div>
      </div>
    </div>
  </div>
);

const PromoAbPanel = ({ promo }) => {
  const v = promo?.variants || { A: { views: 0, submits: 0, rate: 0 }, B: { views: 0, submits: 0, rate: 0 } };
  const noData = (v.A.views + v.B.views) === 0;
  return (
    <div className="bg-white rounded-lg border p-4" data-testid="promo-ab-panel">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Promo popup · A/B copy test</div>
      {noData ? (
        <div className="text-sm text-gray-400">No popup views yet in this window.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <VariantCard label="A" copy="10% off your first wine kit" data={v.A} isLeader={promo?.leader === 'A'} />
          <VariantCard label="B" copy="20% off — limited time (first-time clients)" data={v.B} isLeader={promo?.leader === 'B'} />
        </div>
      )}
    </div>
  );
};

const TrackingTab = () => {
  const [days, setDays] = useState(90);
  const [stats, setStats] = useState(null);
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${sessionStorage.getItem('adminToken')}` };
    axios.get(`${API}/track/stats?days=${days}`, { headers })
      .then((r) => setStats(r.data)).catch(() => setStats(null));
    axios.get(`${API}/track/promo/stats?days=${days}`, { headers })
      .then((r) => setPromo(r.data)).catch(() => setPromo(null));
  }, [days]);

  if (!stats) return <div className="text-gray-400 text-sm">Loading…</div>;
  const main = stats.clicksBySite?.main || 0;
  const corp = stats.clicksBySite?.corp || 0;
  const total = main + corp || 1;
  const dailyKeys = Object.keys(stats.daily || {});
  const dailyMax = Math.max(1, ...dailyKeys.map((d) => (stats.daily[d].main || 0) + (stats.daily[d].corp || 0)));

  return (
    <div className="space-y-4" data-testid="admin-tracking-tab">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div>
          <div className="text-sm text-gray-500">CTA clicks · last {stats.days} days</div>
          <div className="text-2xl font-bold text-gray-900" data-testid="tracking-total">{stats.totalClicks}</div>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="border rounded-md px-3 py-1.5 text-sm" data-testid="tracking-days">
          {[7, 30, 90, 365].map((d) => <option key={d} value={d}>Last {d} days</option>)}
        </select>
      </div>

      <PromoAbPanel promo={promo} />

      <div className="bg-white rounded-lg border p-4">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">thelokal.ca vs corp.thelokal.ca</div>
        <div className="flex h-3 rounded overflow-hidden bg-gray-100">
          <div style={{ width: `${(main / total) * 100}%` }} className="bg-[#FDB913]" />
          <div style={{ width: `${(corp / total) * 100}%` }} className="bg-[#722F37]" />
        </div>
        <div className="flex justify-between mt-2 text-sm">
          <span data-testid="tracking-main"><span className="inline-block w-2 h-2 rounded-full bg-[#FDB913] mr-1" />Main site: <b>{main}</b></span>
          <span data-testid="tracking-corp"><span className="inline-block w-2 h-2 rounded-full bg-[#722F37] mr-1" />Corporate: <b>{corp}</b></span>
        </div>
        {dailyKeys.length > 0 && (
          <div className="flex items-end gap-[2px] h-16 mt-4" data-testid="tracking-daily">
            {dailyKeys.map((d) => {
              const m = stats.daily[d].main || 0; const c = stats.daily[d].corp || 0;
              return (
                <div key={d} title={`${d}: ${m} main · ${c} corp`} className="flex-1 flex flex-col justify-end">
                  <div className="bg-[#722F37]" style={{ height: `${(c / dailyMax) * 64}px` }} />
                  <div className="bg-[#FDB913]" style={{ height: `${(m / dailyMax) * 64}px` }} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Bucket title="Main site · by goal" data={stats.clicksByPathMain} testid="tracking-path-main" />
        <Bucket title="Corporate · by path" data={stats.clicksByPathCorp} testid="tracking-path-corp" />
        <Bucket title="By UTM source" data={stats.clicksBySource} testid="tracking-source" />
        <Bucket title="By UTM campaign" data={stats.clicksByCampaign} testid="tracking-campaign" />
        <Bucket title="By UTM medium" data={stats.clicksByMedium} testid="tracking-medium" />
        <Bucket title="By page" data={stats.clicksByPage} testid="tracking-page" />
        <div className="lg:col-span-2"><Bucket title="Top CTAs" data={stats.clicksByCta} testid="tracking-cta" /></div>
      </div>

      <div className="bg-white rounded-lg border overflow-x-auto">
        <div className="px-4 pt-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Form submissions by source (in window)</div>
        <table className="w-full text-sm" data-testid="tracking-submissions">
          <thead><tr className="text-left text-xs text-gray-600 uppercase"><th className="px-4 py-2">Form</th><th className="px-4 py-2">In window</th><th className="px-4 py-2">All time</th><th className="px-4 py-2">Top sources</th><th className="px-4 py-2">Top campaigns</th></tr></thead>
          <tbody>
            {stats.submissions.map((s) => (
              <tr key={s.collection} className="border-t">
                <td className="px-4 py-2 font-medium">{s.label}</td>
                <td className="px-4 py-2">{s.inWindow}</td>
                <td className="px-4 py-2 text-gray-500">{s.total}</td>
                <td className="px-4 py-2 text-xs text-gray-600">{Object.entries(s.bySource).map(([k, v]) => `${k} (${v})`).join(', ') || '—'}</td>
                <td className="px-4 py-2 text-xs text-gray-600">{Object.entries(s.byCampaign).map(([k, v]) => `${k} (${v})`).join(', ') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TrackingTab;
